import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Mic capture -> Whisper transcription, and OpenAI TTS playback via existing voice-assistant routes
export function useVoiceIO({ onTranscript, onError }) {
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [muted, setMuted] = useState(false);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setSpeaking(false);
  }, []);

  const startListening = useCallback(async () => {
    stopSpeaking();
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      onError?.('Voice input is not supported in this browser. Please type your request.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setListening(false);
        const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
        if (blob.size < 1200) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'speech.webm');
          const { data } = await axios.post(`${API}/api/voice-assistant/transcribe`, fd, { timeout: 60000 });
          if (data.text?.trim()) onTranscript?.(data.text.trim());
          else onError?.("I couldn't hear anything. Please try again or type.");
        } catch {
          onError?.('Voice transcription failed. You can continue by typing.');
        } finally {
          setTranscribing(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setListening(true);
    } catch {
      onError?.('Microphone permission was denied. You can continue by typing.');
    }
  }, [onTranscript, onError, stopSpeaking]);

  const stopListening = useCallback(() => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  }, []);

  const speak = useCallback(async (text) => {
    if (muted || !text) return;
    stopSpeaking();
    try {
      const { data } = await axios.post(`${API}/api/voice-assistant/speak`, { text: text.slice(0, 1500), voice: 'nova' }, { timeout: 60000 });
      if (!data.audio_base64) return;
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      setSpeaking(true);
      await audio.play();
    } catch {
      setSpeaking(false);
    }
  }, [muted, stopSpeaking]);

  useEffect(() => () => {
    stopSpeaking();
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, [stopSpeaking]);

  return { listening, transcribing, speaking, muted, setMuted, startListening, stopListening, speak, stopSpeaking };
}
